import type { UploadedItem } from '~~/shared/items/uploadedItem'
import type { UploadInitRequest, UploadInitResponse, UploadConfirmRequest } from '~~/shared/items/uploadFlow'
import type { useApi } from '~/composables/useApi'

/** A OneDrive re-upload reference (AlexAmin/marcus#845), carried as provenance on the new row. */
export interface OneDriveRef { itemId: string; driveId?: string | null; url?: string | null }

export interface SendUploadOptions {
  apiFetch: ReturnType<typeof useApi>['apiFetch']
  path: string
  title?: string
  onedrive?: OneDriveRef
  signal: AbortSignal
}

/** sha256 of a file's bytes, computed client-side (Web Crypto) — the dedupe key `upload-init`
 *  checks BEFORE anything is written to GCS (§4, Deduplicación; AlexAmin/marcus#843). */
async function sha256Hex(file: File): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer())
  return Array.from(new Uint8Array(digest)).map(b => b.toString(16).padStart(2, '0')).join('')
}

/**
 * One file over the wire — the upload TRANSPORT, split out of the indexing store (MAR-948). It
 * knows nothing about jobs or the Activity Center: it returns the row the server created (or already
 * held), `null` for a request that answered without one, and throws for one that failed.
 *
 * Direct-to-GCS as of AlexAmin/marcus#843: `web` never buffers these bytes. The browser hashes the
 * file, asks `upload-init` where to put it (or learns the org already has it), POSTs straight to
 * the signed policy (AlexAmin/marcus#845 — a POST policy, not a PUT; see `getSignedUploadPolicy`'s
 * header for why), then asks `upload-confirm` to verify the object landed and start indexing. A
 * local dev box with no bucket configured degrades to the old multipart endpoint (`mode: 'inline'`),
 * exactly the way every other storage-optional path in this app already degrades.
 */
export async function sendUpload(file: File, opts: SendUploadOptions): Promise<UploadedItem | null> {
  const { apiFetch, path, title, onedrive, signal } = opts
  const contentSha256 = await sha256Hex(file)
  const contentType = file.type || 'application/octet-stream'
  const init = await apiFetch<UploadInitResponse>('/api/items/upload-init', {
    method: 'POST',
    body: {
      fileName: file.name,
      contentType,
      size: file.size,
      contentSha256,
    } satisfies UploadInitRequest,
    signal,
  })

  if (init.mode === 'duplicate') return init.item as UploadedItem

  if (init.mode === 'inline') {
    // No object store locally — fall back to the original buffered endpoint.
    const formData = new FormData()
    formData.append('path', path)
    if (title) formData.append('title', title)
    if (onedrive?.itemId) formData.append('onedrive_item_id', onedrive.itemId)
    if (onedrive?.driveId) formData.append('onedrive_drive_id', onedrive.driveId)
    if (onedrive?.url) formData.append('onedrive_url', onedrive.url)
    formData.append('file', file)
    const created = await apiFetch<UploadedItem[]>('/api/items/upload', { method: 'POST', body: formData, signal })
    return created?.[0] ?? null
  }

  // `mode === 'direct'`: POST straight to GCS against the signed policy (AlexAmin/marcus#845 — a
  // POST policy, not a PUT). Not `apiFetch` — this is a foreign origin (the bucket), not our own
  // API. The policy's fields MUST precede the `file` field: GCS ignores anything that arrives in the
  // multipart body after it.
  const gcsForm = new FormData()
  for (const [key, value] of Object.entries(init.uploadFields)) gcsForm.append(key, value)
  gcsForm.append('file', file)
  const postRes = await fetch(init.uploadUrl, { method: 'POST', body: gcsForm, signal })
  if (!postRes.ok) return null

  const item = await apiFetch<UploadedItem>('/api/items/upload-confirm', {
    method: 'POST',
    body: {
      storage_path: init.storage_path,
      fileName: file.name,
      // The server's own echoed Content-Type (the signed condition), not the client's guess — same
      // trust rule the old PUT flow's `requiredContentType` field followed.
      contentType: init.uploadFields['Content-Type'] || contentType,
      path,
      title,
      contentSha256,
      reuploadItemId: init.reuploadItemId,
      onedriveItemId: onedrive?.itemId,
      onedriveDriveId: onedrive?.driveId,
      onedriveUrl: onedrive?.url,
    } satisfies UploadConfirmRequest,
    signal,
  })
  return item ?? null
}
